import api from './api';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface User {
  id: number;
  name: string;
  email: string;
  role: {
    id: number;
    name: string;
    permissions: string[];
  } | null;
  type?: 'internal' | 'partner';
}

export interface PartnerUser {
  id: number;
  name: string;
  organization_name?: string;
  email: string;
  logo_url?: string;
  contact_person?: string;
  type: 'partner';
  organization?: {
    name: string;
    logo?: string;
    contact_person: string;
    phone?: string;
  };
}

export interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    user: User | PartnerUser;
    token: string;
    token_type: string;
    expires_in: number;
  };
}

export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  errors?: any;
}

class AuthService {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    // Try internal user login first
    // Note: 401 error in console is expected if user is a partner - this is normal behavior
    try {
      const response = await api.post<LoginResponse>('/auth/login', credentials);
      
      if (response.data.success) {
        localStorage.setItem('token', response.data.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.data.user));
        localStorage.setItem('userType', 'internal');
        return response.data;
      }
    } catch (error: any) {
      // If internal login fails with 401, try partner login
      if (error.response?.status === 401) {
        try {
          const partnerResponse = await api.post<LoginResponse>('/partner/auth/login', credentials);
          
          if (partnerResponse.data.success) {
            localStorage.setItem('token', partnerResponse.data.data.token);
            localStorage.setItem('user', JSON.stringify(partnerResponse.data.data.user));
            localStorage.setItem('userType', 'partner');
            return partnerResponse.data;
          }
        } catch (partnerError: any) {
          console.error('❌ Both login attempts failed');
          throw new Error(partnerError.response?.data?.message || 'Email atau password salah');
        }
      }
      throw error;
    }
    
    throw new Error('Login failed');
  }

  async logout(): Promise<void> {
    try {
      const userType = this.getUserType();
      const endpoint = userType === 'partner' ? '/partner/auth/logout' : '/auth/logout';
      await api.post(endpoint);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local storage
      this.clearAuth();
    }
  }

  async getCurrentUser(): Promise<User | PartnerUser | null> {
    try {
      const userType = this.getUserType();
      const endpoint = userType === 'partner' ? '/partner/auth/me' : '/auth/me';
      const response = await api.get<ApiResponse<{ user: User | PartnerUser }>>(endpoint);
      
      if (response.data.success && response.data.data) {
        const user = response.data.data.user;
        localStorage.setItem('user', JSON.stringify(user));
        return user;
      }
    } catch (error) {
      console.error('Get current user error:', error);
      this.clearAuth();
    }
    return null;
  }

  async refreshToken(): Promise<string | null> {
    try {
      const userType = this.getUserType();
      const endpoint = userType === 'partner' ? '/partner/auth/refresh' : '/auth/refresh';
      const response = await api.post<ApiResponse<{ token: string }>>(endpoint);
      
      if (response.data.success && response.data.data) {
        const token = response.data.data.token;
        localStorage.setItem('token', token);
        return token;
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      this.clearAuth();
    }
    return null;
  }

  getStoredUser(): User | PartnerUser | null {
    try {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('Error parsing stored user:', error);
      return null;
    }
  }

  getStoredToken(): string | null {
    return localStorage.getItem('token');
  }

  isAuthenticated(): boolean {
    return !!this.getStoredToken();
  }

  clearAuth(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userType');
  }

  hasPermission(permission: string): boolean {
    const user = this.getStoredUser();
    // Only internal users have role-based permissions
    return (user && 'role' in user && user?.role?.permissions.includes(permission)) || false;
  }

  isAdmin(): boolean {
    const user = this.getStoredUser();
    return (user && 'role' in user && user?.role?.name === 'Admin') || false;
  }

  getUserType(): 'internal' | 'partner' {
    return localStorage.getItem('userType') as 'internal' | 'partner' || 'internal';
  }

  isPartner(): boolean {
    return this.getUserType() === 'partner';
  }

  isInternal(): boolean {
    return this.getUserType() === 'internal';
  }
}

const authService = new AuthService();
export default authService;
